import { useMemo, useEffect, useState, useRef } from "react";
import { ArrowLeft, Send, Trash2, Eye, Download, MoreVertical, X, AlertCircle } from "lucide-react";
import { useInvoiceDraft } from "./hooks/useInvoiceDraft";
import { calculateInvoiceSummary } from "./utils/invoiceSummary";
import { validateInvoiceForm, scrollToFirstError } from "../../utils/validation";
import { showSmtpWarning } from "../../utils/smtpValidation";
import { useToast } from "../../hooks/useToast";
import { useConfirm } from "../../hooks/useConfirm";
import InvoiceHeader from "./components/InvoiceHeader";
import BillToSectionPremium from "./components/BillToSectionPremium";
import LineItemsEditor from "./components/LineItemsEditor";
import TotalsSection from "./components/TotalsSection";
import NotesSection from "./components/NotesSection";
import PaymentSection from "./components/PaymentSection";
import LivePreview2026 from "./components/LivePreview2026";
import AutoSaveIndicator from "./components/AutoSaveIndicator";
import FullInvoicePreview from "./components/FullInvoicePreview";
import DownloadInvoiceModal from "./components/DownloadInvoiceModal";
import Toast from "../../ui/Toast";
import ConfirmDialog from "../../ui/ConfirmDialog";
import { generateInvoicePDF } from "../../utils/pdfGenerator";
import invoiceService from "../../services/invoiceService";

// ── Friendly error messages ────────────────────────────────────────────────
function getFriendlyInvoiceError(err) {
  const msg = err?.message || String(err);
  if (msg.includes('invoice_number') || msg.includes('UNIQUE constraint') || msg.includes('already used by another invoice')) {
    const match = msg.match(/Invoice number "([^"]+)"/);
    if (match) return `Invoice number "${match[1]}" is already in use. Please change it to a unique number.`;
    return 'That invoice number is already used by another invoice. Please choose a different number.';
  }
  if (msg.includes('Client is required')) return 'Please select a client before sending the invoice.';
  if (msg.includes('line item')) return 'Please add at least one line item before sending.';
  if (msg.includes("Error invoking remote method")) {
    const clean = msg.replace(/Error invoking remote method '[^']+': /, '');
    return getFriendlyInvoiceError({ message: clean });
  }
  if (msg.startsWith('SqliteError:')) return 'Something went wrong saving the invoice. Please try again.';
  return msg;
}

export default function InvoiceLayout2026({ invoiceId = null, initialAction = null, onBack, onNavigate }) {
  const { 
    invoice, 
    updateField,
    sendInvoice,
    resendInvoice,
    deleteInvoice,
    saveDraft,
    isLoading,
    isSaving, 
    lastSaved,
    error,
    hasUnsavedChanges,
  } = useInvoiceDraft(invoiceId);

  const { toast, hideToast, success, error: showError, warning, info } = useToast();
  const { dialog, confirm } = useConfirm();

  const invoiceSummary = useMemo(() => calculateInvoiceSummary(invoice), [invoice]);
  const validation = useMemo(() => validateInvoiceForm(invoice), [invoice]);

  const [isResending, setIsResending] = useState(false);
  const isEditable = invoice.status === "draft";
  const isPartiallyEditable = invoice.status === "sent";
  const isReadOnly = invoice.status === "paid" || invoice.status === "cancelled";
  const canSend = isEditable && validation.canSend;

  const [showInlineValidation, setShowInlineValidation] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const headerRef = useRef(null);
  const sentinelRef = useRef(null);
  const paymentSectionRef = useRef(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Handle initial action
  useEffect(() => {
    if (!initialAction || isLoading) return;
    const timer = setTimeout(() => {
      switch (initialAction) {
        case 'preview': setShowFullPreview(true); break;
        case 'download': setShowDownloadModal(true); break;
        case 'recordPayment':
          if (paymentSectionRef.current) {
            paymentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            paymentSectionRef.current.classList.add('ring-2', 'ring-indigo-500', 'ring-offset-2');
            setTimeout(() => paymentSectionRef.current?.classList.remove('ring-2', 'ring-indigo-500', 'ring-offset-2'), 2000);
          }
          break;
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [initialAction, isLoading]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && isEditable) {
        e.preventDefault();
        handleSendInvoice();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "p") {
        e.preventDefault();
        handlePreview();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isEditable, invoice]);

  // Sticky header observer
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsHeaderSticky(!entry.isIntersecting),
      { threshold: 0, rootMargin: '0px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, []);

  /* ═══════════════════════════════════════════════════════════
     ACTIONS
     ═══════════════════════════════════════════════════════════ */
  const handleBack = async () => {
    if (hasUnsavedChanges) {
      try {
        await saveDraft();
      } catch (err) {
        console.error("Failed to save before navigation:", err);
      }
    }
    if (onBack) onBack();
  };

  const handleSendInvoice = async () => {
    const canProceed = await showSmtpWarning(showError, confirm, async () => {
      try {
        await saveDraft();
      } catch (err) {
        console.error('[Invoice] Failed to save draft before navigation:', err);
      }
      if (onNavigate) onNavigate("settings");
    });
    if (!canProceed) return;

    const validation = validateInvoiceForm(invoice);
    if (!validation.canSend) {
      setShowInlineValidation(true);
      scrollToFirstError(validation.errors);
      return;
    }

    const confirmed = await confirm({
      title: "Send Invoice?",
      message: "Once sent, line items and tax will be locked. You can still add payments and update notes.",
      confirmText: "Send Invoice",
      cancelText: "Cancel",
      variant: "primary",
    });
    if (!confirmed) return;

    try {
      const result = await sendInvoice();
      if (result?.emailResult) {
        if (result.emailResult.success) {
          success(`Invoice sent successfully! Email delivered to ${invoice.client.email}`);
        } else if (result.emailResult.skipped) {
          success("Invoice sent successfully! (Email not configured)");
        } else {
          success("Invoice sent successfully!");
          warning(`Email delivery failed: ${result.emailResult.message}`);
        }
      } else {
        success("Invoice sent successfully!");
      }
    } catch (err) {
      showError(getFriendlyInvoiceError(err));
    }
  };

  const handleDeleteInvoice = async () => {
    const confirmed = await confirm({
      title: "Delete Draft Invoice?",
      message: "This action cannot be undone. The invoice will be permanently deleted.",
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteInvoice();
      success("Invoice deleted successfully");
      if (onBack) onBack();
    } catch (err) {
      showError(`Failed to delete invoice: ${err.message}`);
    }
  };

  const handleResendInvoice = async () => {
    const canProceed = await showSmtpWarning(showError, confirm, async () => {
      if (onNavigate) onNavigate("settings");
    });
    if (!canProceed) return;

    const confirmed = await confirm({
      title: "Resend Invoice?",
      message: `Resend invoice ${invoice.invoiceNumber || invoice.invoice_number} to ${invoice.client?.email || 'the client'}?`,
      confirmText: "Resend Invoice",
      cancelText: "Cancel",
      variant: "primary",
    });
    if (!confirmed) return;

    setIsResending(true);
    try {
      const result = await resendInvoice();
      if (result?.emailResult) {
        if (result.emailResult.success) {
          success(`Invoice resent successfully! Email delivered to ${invoice.client.email}`);
        } else if (result.emailResult.skipped) {
          success("Invoice resent successfully! (Email not configured)");
        } else {
          success("Invoice resent successfully!");
          warning(`Email delivery failed: ${result.emailResult.message}`);
        }
      } else {
        success("Invoice resent successfully!");
      }
    } catch (err) {
      showError(`Failed to resend invoice: ${err.message}`);
    } finally {
      setIsResending(false);
    }
  };

  const handlePreview = async () => {
    try {
      await saveDraft();
      setShowFullPreview(true);
    } catch (err) {
      console.error('Failed to prepare preview:', err);
      setShowFullPreview(true);
    }
  };

  const handleDownload = () => {
    // Allow download at ANY stage (draft or sent) - Modern SaaS UX
    setShowDownloadModal(true);
  };

  const handleDownloadWithOptions = async () => {
    try {
      info("Generating PDF...");
      
      // Get auth tokens
      const authToken = sessionStorage.getItem('sessionToken');
      const deviceToken = sessionStorage.getItem('deviceToken');
      
      if (!authToken || !deviceToken) {
        showError('Authentication required. Please refresh the page.');
        return;
      }
      
      // Fetch company settings and theme
      const settingsService = await import('../../services/settings.service');
      const theme = await settingsService.default.getActiveTheme();
      const companySettings = theme?.company || theme;
      
      // Fetch payments for sent/paid invoices
      let payments = [];
      if ((invoice.status === 'sent' || invoice.status === 'paid') && invoice.id) {
        try {
          payments = await invoiceService.listPayments(invoice.id);
        } catch (err) {
          console.error('Failed to load payments for PDF:', err);
        }
      }
      
      // Prepare invoice data with proper field mapping
      const invoiceData = {
        ...invoice,
        payments: payments || [],
        // Ensure client data is properly structured
        client: {
          name: invoice.client?.name || invoice.client_name || '—',
          email: invoice.client?.email || invoice.client_email || '',
          phone: invoice.client?.phone || invoice.client_phone || '',
          gstin: invoice.client?.gstin || invoice.client_gstin || '',
          billing_address: invoice.client?.billing_address || invoice.client_billing_address || '',
          city: invoice.client?.city || invoice.client_city || '',
          state: invoice.client?.state || invoice.client_state || '',
          pincode: invoice.client?.pincode || invoice.client_pincode || ''
        },
        // Ensure proper field mapping
        invoiceNumber: invoice.invoiceNumber || invoice.invoice_number,
        issueDate: invoice.issueDate || invoice.issue_date,
        dueDate: invoice.dueDate || invoice.due_date,
        items: invoice.items || [],
        total: invoice.total || 0,
        subtotal: invoice.subtotal || 0,
        tax_total: invoice.tax_total || 0,
        discount: invoice.discount || 0,
        shipping: invoice.shipping || 0,
        round_off: invoice.round_off || invoice.roundOff || 0,
        notes: invoice.notes || ''
      };
      
      // Call backend to generate PDF (same as email)
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${API_URL}/api/email/generate-invoice-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'X-Device-Token': deviceToken
        },
        body: JSON.stringify({
          invoiceData,
          companySettings
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to generate PDF');
      }
      
      // Get PDF blob
      const pdfBlob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoice.invoiceNumber || invoice.invoice_number || 'DRAFT'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      success("PDF downloaded successfully");
      setShowDownloadModal(false);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      showError(error.message || "Failed to generate PDF");
    }
  };

  /* ═══════════════════════════════════════════════════════════
     RENDER - 2026 UX REDESIGN
     ═══════════════════════════════════════════════════════════ */
  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-50 to-slate-100/40">
        <div ref={sentinelRef} className="h-px" aria-hidden="true" />

        {/* ═══ HEADER - Simplified Action Hierarchy ═══ */}
        <div 
          ref={headerRef}
          className={`sticky top-0 z-20 transition-all duration-200 ${
            isHeaderSticky 
              ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200' 
              : 'bg-white border-b border-slate-200'
          }`}
        >
          <div className={`px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all ${
            isHeaderSticky ? 'py-2.5' : 'py-3'
          }`}>
            {/* Left: Context */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {onBack && (
                <button
                  onClick={handleBack}
                  disabled={isSaving}
                  className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Back to list"
                >
                  <ArrowLeft size={18} />
                </button>
              )}
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-semibold text-slate-900 truncate">
                  {invoice.invoiceNumber || "INV-0001"}
                </span>
                <StatusBadge status={invoice.status} />
              </div>
              <div className="hidden lg:block">
                <AutoSaveIndicator lastSaved={lastSaved} isSaving={isSaving} error={error} />
              </div>
            </div>
            
            {/* Right: Clear Action Hierarchy */}
            <div className="hidden lg:flex items-center gap-2">
              {/* Secondary: Preview (Ghost) */}
              <button
                onClick={handlePreview}
                className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150 active:scale-95"
              >
                <Eye size={14} className="inline mr-1.5" />
                Preview
              </button>

              {/* Secondary: Download PDF (Ghost) - Available at ANY stage */}
              <button
                onClick={handleDownload}
                className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150 active:scale-95"
              >
                <Download size={14} className="inline mr-1.5" />
                Download PDF
              </button>

              {/* Tertiary: More Actions (Dropdown) - Only for Draft Delete */}
              {invoice.status === "draft" && (
                <MoreActionsMenu
                  invoice={invoice}
                  onDelete={handleDeleteInvoice}
                />
              )}
              
              {/* Primary: Send Invoice (Dominant) - ENHANCED with Ready State */}
              {invoice.status === "draft" && (
                <button 
                  onClick={handleSendInvoice}
                  disabled={isLoading || isSaving || !canSend}
                  className={`px-5 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                    canSend && !isLoading && !isSaving ? 'hover:shadow-md hover:scale-[1.02]' : ''
                  }`}
                >
                  <Send size={14} className="inline mr-1.5" />
                  {isLoading ? "Sending..." : "Send Invoice"}
                </button>
              )}

              {/* Resend for sent invoices */}
              {(invoice.status === "sent" || invoice.status === "paid") && (
                <button
                  onClick={handleResendInvoice}
                  disabled={isResending}
                  className="px-5 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-sm disabled:opacity-50"
                >
                  <Send size={14} className="inline mr-1.5" />
                  {isResending ? "Resending..." : "Resend"}
                </button>
              )}
            </div>

            {/* Mobile: Menu */}
            <div className="lg:hidden">
              <button
                onClick={() => setShowMobileMenu(true)}
                className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <MoreVertical size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <MobileMenu
            invoice={invoice}
            onClose={() => setShowMobileMenu(false)}
            onPreview={handlePreview}
            onDownload={handleDownload}
            onResend={handleResendInvoice}
            onDelete={handleDeleteInvoice}
            isResending={isResending}
          />
        )}

        {/* ═══ MAIN CONTENT - Two Column Layout ═══ */}
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-5 px-4 sm:px-6 lg:px-8 py-4 lg:py-5">
          
          {/* LEFT: Invoice Editor - Grouped & Progressive */}
          <div className="flex-1 lg:max-w-3xl space-y-3.5">
            
            {/* Read-only / Partially editable notices */}
            {isReadOnly && (
              <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
                This invoice is {invoice.status} and cannot be edited.
              </div>
            )}
            {isPartiallyEditable && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                Invoice sent. Line items and tax are locked.
              </div>
            )}

            {/* Auto-save (mobile) */}
            <div className="lg:hidden">
              <AutoSaveIndicator lastSaved={lastSaved} isSaving={isSaving} error={error} />
            </div>

            {/* ═══ STEP 1: Invoice Details ═══ */}
            <InvoiceCard title="Invoice Details" step="1">
              <InvoiceHeader invoice={invoice} updateField={updateField} disabled={isReadOnly} />
            </InvoiceCard>

            {/* ═══ STEP 2: Client Info ═══ */}
            <InvoiceCard title="Bill To" step="2" error={showInlineValidation && !invoice.client}>
              <BillToSectionPremium
                invoice={invoice}
                updateField={updateField}
                disabled={isReadOnly}
                showValidation={showInlineValidation}
              />
            </InvoiceCard>

            {/* ═══ STEP 3: Line Items ═══ */}
            <InvoiceCard title="Line Items" step="3" error={showInlineValidation && (!invoice.items || invoice.items.length === 0)}>
              <LineItemsEditor
                invoice={invoice}
                updateField={updateField}
                disabled={!isEditable}
                showValidation={showInlineValidation}
              />
            </InvoiceCard>

            {/* ═══ STEP 4: Totals ═══ */}
            <InvoiceCard title="Totals & Review" step="4">
              <TotalsSection invoice={invoice} updateField={updateField} disabled={!isEditable} />
            </InvoiceCard>

            {/* Notes (Collapsed by default) - ENHANCED */}
            <details className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-[1px] transition-all duration-200">
              <summary className="px-4 py-2.5 cursor-pointer hover:bg-slate-50/70 active:bg-slate-100/50 transition-all duration-150 flex items-center justify-between list-none">
                <span className="text-sm font-semibold text-slate-700 group-hover:text-slate-900 transition-colors">Notes & Terms</span>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-md group-hover:bg-slate-200 transition-colors">Optional</span>
                  <svg className="w-4 h-4 text-slate-400 transition-transform duration-200 group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </summary>
              <div className="px-4 pb-3.5 pt-2 border-t border-slate-100/90 bg-gradient-to-b from-slate-50/40 to-transparent">
                <NotesSection invoice={invoice} updateField={updateField} disabled={isReadOnly} />
              </div>
            </details>

            {/* Payment Section */}
            {(invoice.status === "sent" || invoice.status === "paid") && (
              <div ref={paymentSectionRef} className="scroll-mt-24 transition-all duration-300">
                <InvoiceCard title="Record Payment">
                  <PaymentSection
                    invoice={invoice}
                    updateField={updateField}
                    autoExpand={initialAction === 'recordPayment'}
                    onPaymentUpdate={(paymentData) => {
                      updateField("totalPaid", paymentData.totalPaid);
                      updateField("balanceDue", paymentData.balanceDue);
                    }}
                  />
                </InvoiceCard>
              </div>
            )}
          </div>

          {/* RIGHT: Live Preview - Enhanced & Sticky */}
          <div className="hidden lg:block w-96 sticky top-[4.5rem] h-[calc(100vh-6rem)] self-start">
            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
              <LivePreview2026 
                summary={invoiceSummary}
                validation={validation}
                onFullPreview={handlePreview}
                canSend={canSend}
                isEditable={isEditable}
              />
            </div>
          </div>
        </div>

        {/* Mobile: Sticky Bottom Action - ENHANCED */}
        {invoice.status === "draft" && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-lg">
            <div className="px-4 py-3">
              <button 
                onClick={handleSendInvoice}
                disabled={isLoading || isSaving || !canSend}
                className={`w-full px-4 py-3 text-base font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-[0.98] transition-all shadow-sm disabled:opacity-50 ${
                  canSend && !isLoading && !isSaving ? 'hover:shadow-md' : ''
                }`}
              >
                <Send size={18} className="inline mr-2" />
                {isLoading ? "Sending..." : "Send Invoice"}
              </button>
              {!canSend && !isLoading && (
                <p className="text-xs text-center text-slate-500 mt-2 animate-in fade-in duration-200">
                  {validation.errors.client ? "Add a client" : "Add line items"}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showFullPreview && (
        <FullInvoicePreview invoice={invoice} onClose={() => setShowFullPreview(false)} onDownload={handleDownload} />
      )}
      {showDownloadModal && (
        <DownloadInvoiceModal invoice={invoice} onClose={() => setShowDownloadModal(false)} onDownload={handleDownloadWithOptions} />
      )}
      {toast && <Toast type={toast.type} message={toast.message} onClose={hideToast} duration={toast.duration} />}
      {dialog && <ConfirmDialog {...dialog} />}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   HELPER COMPONENTS - 2026 UX
   ═══════════════════════════════════════════════════════════ */

// Status Badge - Minimal & Clear with Smooth Transitions
function StatusBadge({ status }) {
  const styles = {
    draft: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
    sent: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
    paid: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
  };
  const style = styles[status] || styles.draft;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 ${style.bg} ${style.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${style.dot}`}></span>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// Invoice Card - ENHANCED: Depth + Hover Lift + Micro-interactions
function InvoiceCard({ title, step, error, children }) {
  return (
    <div className={`group relative bg-white rounded-xl overflow-hidden transition-all duration-200 ${
      error 
        ? 'shadow-md shadow-red-100/60 ring-1 ring-red-200/80' 
        : 'shadow-sm hover:shadow-lg hover:-translate-y-[2px]'
    }`}>
      {/* Top accent - appears on hover */}
      <div className={`absolute top-0 left-0 right-0 h-0.5 transition-opacity duration-200 ${
        error 
          ? 'bg-gradient-to-r from-red-400 to-red-500 opacity-100' 
          : 'bg-gradient-to-r from-indigo-400 via-indigo-500 to-indigo-600 opacity-0 group-hover:opacity-100'
      }`} />
      
      <div className="px-4 py-2.5 bg-gradient-to-b from-slate-50/90 via-slate-50/50 to-transparent border-b border-slate-100/90 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {step && (
            <div className={`relative flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold transition-all duration-200 ${
              error 
                ? 'bg-gradient-to-br from-red-500 to-red-600 text-white shadow-md shadow-red-200/60 ring-1 ring-red-300/50' 
                : 'bg-gradient-to-br from-indigo-500 via-indigo-600 to-indigo-700 text-white shadow-md shadow-indigo-200/70 ring-1 ring-indigo-400/30 group-hover:shadow-lg group-hover:shadow-indigo-200/80'
            }`}>
              <span className="relative z-10">{step}</span>
              {/* Inner glow */}
              <div className="absolute inset-0.5 rounded-lg bg-white/15" />
            </div>
          )}
          <h3 className="text-sm font-bold text-slate-900 tracking-tight">{title}</h3>
        </div>
        {error && (
          <span className="text-[11px] font-semibold text-red-600 flex items-center gap-1 bg-red-50 px-2.5 py-1 rounded-md shadow-sm">
            <AlertCircle size={11} />
            Required
          </span>
        )}
      </div>
      <div className="px-4 py-3.5">
        {children}
      </div>
    </div>
  );
}

// More Actions Dropdown Menu
function MoreActionsMenu({ invoice, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Only show menu for draft invoices (Delete option)
  if (invoice.status !== "draft") return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
      >
        <MoreVertical size={16} />
      </button>
      
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-xl py-1 z-50">
          <button
            onClick={() => { onDelete(); setOpen(false); }}
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
          >
            <Trash2 size={14} />
            Delete Draft
          </button>
        </div>
      )}
    </div>
  );
}

// Mobile Menu
function MobileMenu({ invoice, onClose, onPreview, onDownload, onResend, onDelete, isResending }) {
  return (
    <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={onClose}>
      <div 
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <span className="text-sm font-medium text-slate-900">Actions</span>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg">
            <X size={18} />
          </button>
        </div>
        <div className="p-2">
          <button
            onClick={() => { onPreview(); onClose(); }}
            className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-3"
          >
            <Eye size={18} />
            <span className="text-sm font-medium">Preview</span>
          </button>
          
          {/* Download - Available at ANY stage */}
          <button
            onClick={() => { onDownload(); onClose(); }}
            className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-3"
          >
            <Download size={18} />
            <span className="text-sm font-medium">Download PDF</span>
          </button>
          
          {(invoice.status === "sent" || invoice.status === "paid") && (
            <button
              onClick={() => { onResend(); onClose(); }}
              disabled={isResending}
              className="w-full text-left px-4 py-3 text-indigo-600 hover:bg-indigo-50 rounded-lg flex items-center gap-3 disabled:opacity-50"
            >
              <Send size={18} />
              <span className="text-sm font-medium">{isResending ? "Resending..." : "Resend"}</span>
            </button>
          )}
          
          {invoice.status === "draft" && (
            <>
              <div className="my-2 border-t border-slate-100" />
              <button
                onClick={() => { onDelete(); onClose(); }}
                className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-3"
              >
                <Trash2 size={18} />
                <span className="text-sm font-medium">Delete Invoice</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
